/**
 * ToolDropdown
 *
 * A dropdown menu that displays toggleable tools for the chat interface
 *
 * Primary responsibilities:
 * - Display a menu of available tools with icons and descriptions
 * - Allow users to toggle tools on/off
 * - Handle paywall restrictions for premium tools
 */

import { FC, useContext, useEffect, useState, useMemo } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { ToolsLibraryDialog } from './tools-library-dialog'
import { useQuickActions } from '@/hooks/useQuickActions'
import {
  IconPhoto,
  IconPhotoFilled,
  IconWorld,
  IconWorldOff,
  IconVideo,
  IconVideoFilled,
  IconBulb,
  IconBulbFilled,
  IconApps,
  IconAppsFilled,
  IconCheck,
  IconRobot,
  IconBoltFilled,
  IconMusic,
  IconBrandThreejs,
  IconChevronRight,
  IconPlus,
  IconDots,
  IconPuzzle
} from '@tabler/icons-react'

import { validatePlanForTools } from '@/lib/subscription'
import { cn } from '@/lib/utils'
import useHotkey from '@/lib/hooks/use-hotkey'
import useSafeTranslation from '@/hooks/useSafeTranslation'
import { getToolIcons } from '@/lib/toolIconMapping'
import { useDelayedPaywall } from '@/hooks/use-delayed-paywall'
import { LLM_LIST } from '@/lib/models/llm/llm-list'

import { Button } from '../ui/button'
import { Switch } from '../ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuShortcut
} from '../ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'

interface ToolInfo {
  id: string
  name: string
  description: string
  toolName: string
  enabledIcon: React.ReactNode
  disabledIcon: React.ReactNode
  requiredModel?: string
  shortcut?: string
}

function isCustomIntegrationTool(tool: { sharing?: string | null; user_id?: string | null }) {
  return Boolean(tool.user_id && tool.sharing !== 'platform')
}

function uniqueToolsById<T extends { id: string }>(items: T[]) {
  return Array.from(new Map(items.map((item) => [item.id, item])).values())
}

const DEFAULT_CHAT_TOOL_IDS = [
  'b3f07a6e-5e01-423e-9f06-ee51830608be',
  'b3f07a6e-5e01-423e-1f05-ee51830608gf',
  'b3f07a6e-5e01-423e-1f05-ee51830608vc',
  'b3f07a6e-5e01-423e-1f05-ee51830608ff',
  'deepResearch',
  'sandbox'
]

export const ToolDropdown: FC = () => {
  const {
    profile,
    tools: unsafeTools,
    platformTools: unsafePlatformTools,
    setIsPaywallOpen,
    allModels
  } = useContext(ChatbotUIContext)
  const scheduleUpgradePaywall = useDelayedPaywall(
    setIsPaywallOpen ? () => setIsPaywallOpen(true) : undefined
  )
  const { chatSettings, selectedTools, setSelectedTools } = useContext(ChatbotUIChatContext)
  const { t } = useSafeTranslation()
  const { quickActions, refreshQuickActions } = useQuickActions()

  // Create a safe version of tools that guarantees every tool has a name property
  const tools = useMemo(() => {
    const mergedTools = [
      ...(Array.isArray(unsafeTools) ? unsafeTools : []),
      ...(Array.isArray(unsafePlatformTools) ? unsafePlatformTools : [])
    ]

    return uniqueToolsById(
      mergedTools.filter(
        (tool) =>
          tool && typeof tool === 'object' && typeof tool.name === 'string' && tool.name.length > 0
      )
    )
  }, [unsafePlatformTools, unsafeTools])

  // State for animating the button when tools change
  const [flash, setFlash] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isToolsLibraryDialogOpen, setIsToolsLibraryDialogOpen] = useState(false)

  // Check if current model supports tools
  const selectedModel =
    allModels.find(
      (x) => x.modelId === chatSettings?.model || x.hostedId === chatSettings?.model
    ) ??
    LLM_LIST.find(
      (x) => x.modelId === chatSettings?.model || x.hostedId === chatSettings?.model
    )

  const supportsTools = selectedModel?.tools

  // Handle flash animation when selectedTools change
  useEffect(() => {
    if (!isOpen) {
      setFlash(true)
      setTimeout(() => {
        setFlash(false)
      }, 500)
    }
  }, [selectedTools, isOpen])

  // Map tools with proper icons (must be before early return)
  const availableToolInfos = useMemo(() => {
    // Create a map of tool_id to position for sorting
    const positionMap = new Map(quickActions.map((qa) => [qa.tool_id, qa.item_position]))

    // Get tools that are in quick actions (user's stored quick access tools)
    const quickActionToolIds = quickActions.map((qa) => qa.tool_id)
    const quickActionTools = tools.filter((tool) => quickActionToolIds.includes(tool.id))

    const modelDefaultToolIds = Array.isArray(selectedModel?.defaultTools)
      ? selectedModel.defaultTools
      : []
    const fallbackToolIds = Array.from(new Set([...modelDefaultToolIds, ...DEFAULT_CHAT_TOOL_IDS]))
    const fallbackTools = fallbackToolIds
      .map((toolId) => tools.find((tool) => tool.id === toolId))
      .filter((tool): tool is (typeof tools)[number] => Boolean(tool))

    const selectedToolIds = new Set(selectedTools.map((tool) => tool.id))
    const selectedCustomTools = tools.filter(
      (tool) => selectedToolIds.has(tool.id) && isCustomIntegrationTool(tool)
    )

    const baseToolSource =
      quickActionTools.length > 0
        ? quickActionTools
        : fallbackTools.length > 0
          ? fallbackTools
          : tools.slice(0, 6)
    const toolSource = uniqueToolsById([...baseToolSource, ...selectedCustomTools])
    const fallbackPositionMap = new Map(fallbackToolIds.map((toolId, index) => [toolId, index]))

    // Sort tools by their position in quick actions or the stable fallback order.
    const sortedTools = [...toolSource].sort((a, b) => {
      const posA = positionMap.get(a.id) ?? 999
      const posB = positionMap.get(b.id) ?? 999
      if (quickActionTools.length === 0) {
        return (fallbackPositionMap.get(a.id) ?? 999) - (fallbackPositionMap.get(b.id) ?? 999)
      }
      return posA - posB
    })

    // Map tools to tool info with icons
    return sortedTools.map((tool) => {
      // Get icons based on tool ID, or use default
      const iconConfig = getToolIcons(tool.id)

      // Default icons for tools without specific mappings
      const defaultIcons = {
        enabledIcon: <IconApps size={20} className="text-black dark:text-white" stroke={2.3} />,
        disabledIcon: <IconApps size={20} className="text-muted-foreground" stroke={2} />
      }

      const { enabledIcon, disabledIcon } = iconConfig || defaultIcons

      // Truncate description to prevent wide dropdown
      const description = tool.description || ''
      const truncatedDescription =
        description.length > 60 ? description.substring(0, 60).trim() + '...' : description

      return {
        id: tool.id,
        name: tool.name,
        description: truncatedDescription,
        toolName: tool.id,
        enabledIcon,
        disabledIcon,
        requiredModel: undefined
      }
    })
  }, [tools, quickActions, selectedModel?.defaultTools, selectedTools])

  // Include More as an action item in the dropdown
  const availableTools = [
    ...availableToolInfos,
    {
      id: 'More',
      name: t('More'),
      description: t('Manage all tools'),
      toolName: 'More',
      enabledIcon: <IconDots size={20} className="text-black dark:text-white" stroke={2.3} />,
      disabledIcon: <IconDots size={20} className="text-muted-foreground" stroke={2} />
    }
  ]

  // If the model doesn't support tools, don't render anything
  if (!supportsTools) return null

  // Don't render if no tools are available
  if (!availableTools || availableTools.length === 0) return null

  const handleToggleTool = async (toolId: string) => {
    if (!toolId) return

    // Special case: More opens tools library dialog
    if (toolId === 'More') {
      setIsToolsLibraryDialogOpen(true)
      setIsOpen(false) // Close the dropdown
      return
    }

    const tool = tools.find((item) => item.id === toolId)
    if (!tool || !chatSettings) return

    if (!validatePlanForTools(profile, [tool], chatSettings.model)) {
      scheduleUpgradePaywall()
      return
    }

    // Toggle tool in selectedTools for current chat (same as sidebar)
    const isSelected = selectedTools.some((selectedTool) => selectedTool.id === tool.id)

    if (isSelected) {
      setSelectedTools(selectedTools.filter((t) => t.id !== tool.id))
    } else {
      setSelectedTools([...selectedTools, tool])
    }
  }

  const isToolEnabled = (toolId: string) => {
    if (!toolId) return false

    // Special case: More is never "enabled" (it's an action, not a toggleable tool)
    if (toolId === 'More') return false

    // Count from selectedTools directly — do not require `tools` catalog to be
    // hydrated (Crew Chat on /your-crew can have selectedTools while tools=[]).
    return selectedTools.some((selectedTool) => selectedTool.id === toolId)
  }

  // Register global hotkeys for the tools
  // useHotkey("alt+w", () => {
  //   const webTool = availableTools.find(t => t.toolName === "Web Browsing")
  //   if (webTool) handleToggleTool(webTool.toolName)
  // })

  // useHotkey("alt+i", () => {
  //   const imageTool = availableTools.find(t => t.toolName === "Create Images")
  //   if (imageTool) handleToggleTool(imageTool.toolName)
  // })

  // useHotkey("alt+v", () => {
  //   const videoTool = availableTools.find(t => t.toolName === "Video Generation")
  //   if (videoTool) handleToggleTool(videoTool.toolName)
  // })

  // useHotkey("alt+t", () => {
  // })

  // Badge must match the checked switches in this menu. selectedTools can
  // hold extras not listed here (stale after mate delete / other surfaces).
  const listedToolIds = new Set(
    availableToolInfos.map(tool => tool.id).filter(Boolean)
  )
  const activeToolsCount =
    listedToolIds.size > 0
      ? selectedTools.filter(tool => listedToolIds.has(tool.id)).length
      : selectedTools.length

  // Keyboard handler for quick tool toggling
  const handleKeyDown = (e: React.KeyboardEvent, toolName: string) => {
    // Space or Enter toggles the tool
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      handleToggleTool(toolName)
    }
  }

  return (
    <>
      <TooltipProvider>
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'relative rounded-lg p-2 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800',
                    activeToolsCount > 0
                      ? 'border border-slate-300/40 bg-slate-100/80 dark:border-slate-600/30 dark:bg-slate-800/20'
                      : 'border border-slate-200/30 dark:border-slate-700/20',
                    flash && activeToolsCount > 0 && 'animate-bounce'
                  )}
                  aria-label={`${t('AI Tools')} (${activeToolsCount} ${t('active')})`}
                >
                  {activeToolsCount > 0 ? (
                    <div className="flex items-center gap-1.5">
                      <IconAppsFilled size={20} className="text-muted-foreground" />
                      <span className="text-sm font-medium text-muted-foreground">
                        {t('Tools')}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <IconApps size={20} className="text-muted-foreground/70" />
                      <span className="text-sm text-muted-foreground/70">{t('Tools')}</span>
                    </div>
                  )}

                  {activeToolsCount > 3 ? (
                    <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-blue-500 text-[10px] font-medium text-white">
                      {activeToolsCount}
                    </span>
                  ) : (
                    /* Small indicator icons for active tools */
                    <div className="absolute -bottom-1 left-1/2 flex -translate-x-1/2 space-x-0.5">
                      {availableTools.map((tool, index) => {
                        if (tool && tool.toolName && isToolEnabled(tool.toolName)) {
                          const translationOffset = index * 4 - (activeToolsCount - 1) * 2
                          return (
                            <div
                              key={tool.toolName}
                              className="size-1.5 rounded-full bg-blue-500"
                              style={{
                                transform: `translateX(${translationOffset}px)`
                              }}
                              // Add tooltip to show which tool the dot represents
                              title={tool.name}
                            />
                          )
                        }
                        return null
                      })}
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>
                {t('AI Tools')} {activeToolsCount > 0 ? `(${activeToolsCount} ${t('active')})` : ''}
              </p>
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent
            align="end"
            className="flex max-h-[60vh] w-[min(320px,calc(100vw-2rem))] min-w-[200px] flex-col overflow-hidden p-0"
            sideOffset={5}
            avoidCollisions={true}
            collisionPadding={20}
          >
            {/* Fixed Header */}
            <div className="border-b bg-popover p-1">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>{t('Quick Actions')}</span>
                {activeToolsCount > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {activeToolsCount} {t('active')}
                  </span>
                )}
              </DropdownMenuLabel>
            </div>

            {/* Scrollable Tools Section */}
            <div className="flex-1 overflow-y-auto p-1">
              {availableTools
                .filter((tool) => tool && tool.toolName && tool.toolName !== 'More')
                .map((tool, index) => {
                  if (!tool || !tool.toolName) return null

                  // Special handling for More (Tools Library)
                  if (tool.toolName === 'More') {
                    return (
                      <DropdownMenuItem
                        key={tool.toolName}
                        className="flex cursor-pointer items-center justify-between gap-2 py-2"
                        onClick={() => handleToggleTool(tool.toolName)}
                        onKeyDown={(e) => handleKeyDown(e, tool.toolName)}
                        tabIndex={0}
                      >
                        <div className="flex items-center gap-2">
                          <div className="shrink-0">{tool.disabledIcon}</div>
                          <div className="flex flex-col">
                            <span className="flex items-center text-sm font-medium">
                              {tool.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {tool.description}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <IconChevronRight size={16} className="text-muted-foreground" />
                        </div>
                      </DropdownMenuItem>
                    )
                  }

                  const enabled = isToolEnabled(tool.toolName)
                  const toolObject = tools.find((item) => item.id === tool.toolName)
                  const isPremium =
                    toolObject &&
                    !validatePlanForTools(profile, [toolObject], chatSettings?.model ?? undefined)

                  return (
                    <DropdownMenuItem
                      key={tool.toolName}
                      className={cn(
                        'flex cursor-pointer items-center justify-between gap-2 py-2',
                        enabled && 'bg-blue-50 dark:bg-blue-900/20'
                      )}
                      onClick={() => handleToggleTool(tool.toolName)}
                      onKeyDown={(e) => handleKeyDown(e, tool.toolName)}
                      tabIndex={0}
                    >
                      <div className="flex items-center gap-2">
                        <div className="shrink-0">
                          {enabled ? tool.enabledIcon : tool.disabledIcon}
                        </div>
                        <div className="flex flex-col">
                          <span className="flex items-center text-sm font-medium">
                            {tool.name}
                            {isPremium && (
                              <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-800 dark:bg-amber-800/30 dark:text-amber-300">
                                {t('PRO')}
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-muted-foreground">{tool.description}</span>
                        </div>
                      </div>
                      <div
                        className="flex items-center"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Switch
                          checked={enabled}
                          onKeyDown={(event) => event.stopPropagation()}
                          onCheckedChange={() => handleToggleTool(tool.toolName)}
                        />
                        {/* <DropdownMenuShortcut>{tool.shortcut || `Alt+${index + 1}`}</DropdownMenuShortcut> */}
                      </div>
                    </DropdownMenuItem>
                  )
                })}
            </div>

            {/* Fixed Footer - More */}
            <div className="border-t bg-popover p-1">
              {availableTools
                .filter((tool) => tool && tool.toolName === 'More')
                .map((tool) => {
                  if (tool.toolName === 'More') {
                    return (
                      <DropdownMenuItem
                        key={tool.toolName}
                        className="flex cursor-pointer items-center justify-between gap-2 py-2"
                        onClick={() => handleToggleTool(tool.toolName)}
                        onKeyDown={(e) => handleKeyDown(e, tool.toolName)}
                        tabIndex={0}
                      >
                        <div className="flex items-center gap-2">
                          <div className="shrink-0">{tool.disabledIcon}</div>
                          <div className="flex flex-col">
                            <span className="flex items-center text-sm font-medium">
                              {tool.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {tool.description}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <IconChevronRight size={16} className="text-muted-foreground" />
                        </div>
                      </DropdownMenuItem>
                    )
                  }
                  return null
                })}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </TooltipProvider>

      {/* Tools Library Dialog */}
      <ToolsLibraryDialog
        isOpen={isToolsLibraryDialogOpen}
        onClose={() => setIsToolsLibraryDialogOpen(false)}
        onQuickActionsChange={refreshQuickActions}
      />
    </>
  )
}
